import React from 'react';
import { VaultKeTheme } from '../../utils/theme';

const Status: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
    },
    main: {
      paddingTop: '80px',
      paddingBottom: VaultKeTheme.spacing.xxl,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: `0 ${VaultKeTheme.spacing.xl}`,
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h1,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    statusCard: {
      ...VaultKeTheme.components.card,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    statusIndicator: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: VaultKeTheme.spacing.md,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    statusDot: {
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: VaultKeTheme.colors.success,
      animation: 'pulse 2s infinite',
    },
    statusText: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.success,
    },
    uptime: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.primary,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    uptimeLabel: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
    services: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: VaultKeTheme.spacing.xl,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    service: {
      ...VaultKeTheme.components.card,
    },
    serviceHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: VaultKeTheme.spacing.md,
    },
    serviceName: {
      ...VaultKeTheme.typography.h5,
      color: VaultKeTheme.colors.text,
    },
    serviceStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: VaultKeTheme.spacing.sm,
    },
    serviceDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
    },
    serviceStatusText: {
      ...VaultKeTheme.typography.caption,
      fontWeight: '600',
    },
    serviceDesc: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.md,
    },
    incidents: {
      ...VaultKeTheme.components.card,
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    incidentsTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    noIncidents: {
      textAlign: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    noIncidentsIcon: {
      fontSize: '3rem',
      marginBottom: VaultKeTheme.spacing.md,
    },
    noIncidentsTitle: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    noIncidentsText: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
    },
    subscribe: {
      ...VaultKeTheme.components.card,
      textAlign: 'center',
    },
    subscribeTitle: {
      ...VaultKeTheme.typography.h3,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subscribeText: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subscribeInput: {
      ...VaultKeTheme.components.input,
      marginBottom: VaultKeTheme.spacing.md,
      maxWidth: '300px',
    },
    subscribeButton: {
      ...VaultKeTheme.components.button.primary,
    },
  };

  const services = [
    {
      name: 'Web Application',
      status: 'operational',
      description: 'Main web platform for chama management',
    },
    {
      name: 'Mobile API',
      status: 'operational',
      description: 'API endpoints for mobile applications',
    },
    {
      name: 'Payment Processing',
      status: 'operational',
      description: 'M-Pesa and card payment integrations',
    },
    {
      name: 'Database',
      status: 'operational',
      description: 'Primary data storage and retrieval',
    },
    {
      name: 'Email Service',
      status: 'operational',
      description: 'Notification and communication services',
    },
    {
      name: 'File Storage',
      status: 'operational',
      description: 'Document and media file storage',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return VaultKeTheme.colors.success;
      case 'degraded':
        return VaultKeTheme.colors.warning;
      case 'outage':
        return VaultKeTheme.colors.error;
      default:
        return VaultKeTheme.colors.textSecondary;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational':
        return 'Operational';
      case 'degraded':
        return 'Degraded';
      case 'outage':
        return 'Outage';
      default:
        return 'Unknown';
    }
  };

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.container}>
          <div style={styles.header}>
            <h1 style={styles.title}>System Status</h1>
            <p style={styles.subtitle}>
              Real-time status of VaultKe services and infrastructure.
              We monitor our systems 24/7 to ensure reliable service.
            </p>
          </div>

          <div style={styles.statusCard}>
            <div style={styles.statusIndicator}>
              <div style={styles.statusDot}></div>
              <div style={styles.statusText}>All Systems Operational</div>
            </div>
            <div style={styles.uptime}>99.9%</div>
            <div style={styles.uptimeLabel}>Uptime (30 days)</div>
          </div>

          <div style={styles.services}>
            {services.map((service, index) => (
              <div key={index} style={styles.service}>
                <div style={styles.serviceHeader}>
                  <h4 style={styles.serviceName}>{service.name}</h4>
                  <div style={styles.serviceStatus}>
                    <div
                      style={{
                        ...styles.serviceDot,
                        backgroundColor: getStatusColor(service.status),
                      }}
                    ></div>
                    <span
                      style={{
                        ...styles.serviceStatusText,
                        color: getStatusColor(service.status),
                      }}
                    >
                      {getStatusText(service.status)}
                    </span>
                  </div>
                </div>
                <p style={styles.serviceDesc}>{service.description}</p>
              </div>
            ))}
          </div>

          <div style={styles.incidents}>
            <h3 style={styles.incidentsTitle}>Recent Incidents</h3>
            <div style={styles.noIncidents}>
              <div style={styles.noIncidentsIcon}>✅</div>
              <h4 style={styles.noIncidentsTitle}>All Clear</h4>
              <p style={styles.noIncidentsText}>
                No incidents reported in the last 90 days. Our systems are running smoothly.
              </p>
            </div>
          </div>

          <div style={styles.subscribe}>
            <h3 style={styles.subscribeTitle}>Stay Informed</h3>
            <p style={styles.subscribeText}>
              Get notified about system updates, maintenance, and any service disruptions.
            </p>
            <input
              style={styles.subscribeInput}
              type="email"
              placeholder="Enter your email"
            />
            <button style={styles.subscribeButton}>Subscribe to Updates</button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Status;